import { useCallback, useEffect, useRef } from "react";

import {
  createSequentialPoller,
  type SequentialPollContext,
  type SequentialPoller,
} from "../utils/sequentialPoller.mjs";

interface UseSequentialPollingOptions<T> {
  enabled?: boolean;
  intervalMs: number;
  initialDelayMs?: number;
  pauseWhenHidden?: boolean;
  errorBackoffMultiplier?: number;
  maxErrorIntervalMs?: number | null;
  request: (context: SequentialPollContext) => Promise<T>;
  onSuccess?: (value: T, context: SequentialPollContext) => void;
  onError?: (error: unknown, context: SequentialPollContext) => void;
}

export function useSequentialPolling<T>({
  enabled = true,
  intervalMs,
  initialDelayMs = 0,
  pauseWhenHidden = true,
  errorBackoffMultiplier = 1,
  maxErrorIntervalMs = null,
  request,
  onSuccess,
  onError,
}: UseSequentialPollingOptions<T>): () => void {
  const requestRef = useRef(request);
  const successRef = useRef(onSuccess);
  const errorRef = useRef(onError);
  const pollerRef = useRef<SequentialPoller | null>(null);

  useEffect(() => {
    requestRef.current = request;
    successRef.current = onSuccess;
    errorRef.current = onError;
  }, [request, onSuccess, onError]);

  useEffect(() => {
    if (!enabled) {
      pollerRef.current?.stop();
      pollerRef.current = null;
      return;
    }

    const poller = createSequentialPoller({
      intervalMs,
      initialDelayMs,
      pauseWhenHidden,
      errorBackoffMultiplier,
      maxErrorIntervalMs,
      request: (context) => requestRef.current(context),
      onSuccess: (value, context) => successRef.current?.(value, context),
      onError: (error, context) => errorRef.current?.(error, context),
    });
    pollerRef.current = poller;
    poller.start();

    return () => {
      poller.stop();
      if (pollerRef.current === poller) pollerRef.current = null;
    };
  }, [enabled, errorBackoffMultiplier, initialDelayMs, intervalMs, maxErrorIntervalMs, pauseWhenHidden]);

  return useCallback(() => {
    pollerRef.current?.trigger();
  }, []);
}
